/* Fixed-source, key-free read-only connectors for Agent Ordinary.
   Never accepts custom URL origins or performs writes to external services. */
type R=Record<string,unknown>;
const finite=(n:unknown):number|null=>{
 const v=typeof n==='number'?n:typeof n==='string'?Number(n.trim()):NaN;
 return Number.isFinite(v)?v:null;
};
const clamp=(n:number)=>Math.max(0,Math.min(100,Math.round(n*100)/100));
const clean=(n:unknown)=>String(n??'').trim();
const srcWhitelist=[
 'https://www.okx.com/api/v5/',
 'https://www.okx.com/api/v5/public/',
 'https://api.github.com/',
 'https://en.wikipedia.org/w/api.php',
 'https://site.api.espn.com/',
 'https://raw.githubusercontent.com/tradermonty/uptrend-dashboard/main/data/',
 'https://tradermonty.github.io/market-breadth-analysis/',
 'https://www.googleapis.com/pagespeedonline/v5/runPagespeed'
];
export async function getPublic(url:string,bytes=1400000,timeout=6500):Promise<{url:string;text:string;fetchedAt:string}>{
 if(!srcWhitelist.some(prefix=>url.startsWith(prefix)))throw new Error('external_source_not_allowlisted');
 const controller=new AbortController();
 const timer=setTimeout(()=>controller.abort(),Math.min(timeout,8000));
 try{
  const r=await fetch(url,{method:'GET',redirect:'error',signal:controller.signal,
   headers:{'Accept':'application/json, text/csv, text/plain, */*',
    'User-Agent':'AgentOrdinary-ReadOnlyResearch/1.0 (source-attributed; noncommercial)'}});
  if(!r.ok)throw new Error('free_source_http_'+r.status);
  const length=Number(r.headers.get('content-length')||0);
  if(length>bytes)throw new Error('free_source_response_too_large');
  const reader=r.body?.getReader();
  if(!reader)throw new Error('free_source_empty_body');
  let size=0;const parts:Uint8Array[]=[];
  while(true){
   const item=await reader.read();if(item.done)break;
   size+=item.value.byteLength;
   if(size>bytes){await reader.cancel();throw new Error('free_source_response_too_large');}
   parts.push(item.value);
  }
  const all=new Uint8Array(size);let cursor=0;
  for(const b of parts){all.set(b,cursor);cursor+=b.length;}
  return {url,text:new TextDecoder().decode(all),fetchedAt:new Date().toISOString()};
 }finally{clearTimeout(timer);}
}
const parse=async(url:string,limit=1000000):Promise<{source:string;fetchedAt:string;data:any}>=>{
 const s=await getPublic(url,limit);let data:any;
 try{data=JSON.parse(s.text)}catch{throw new Error('free_source_invalid_json');}
 return {source:s.url,fetchedAt:s.fetchedAt,data};
};
export async function marketPrices(input:unknown){
 const d=input&&typeof input==='object'&&!Array.isArray(input)?input as R:{};
 const operation=clean(d.operation||'ticker');
 const instId=clean(d.instId||'BTC-USDT').toUpperCase();
 if(!/^[A-Z0-9]{2,18}-[A-Z0-9]{2,15}(?:-SWAP|-\d{6}-\d{3,7}-[CP])?$/.test(instId))
  throw new Error('invalid_OKX_instrument');
 const endpoints:Record<string,string>={
  ticker:'/market/ticker?instId=',candles:'/market/candles?limit=50&instId=',
  orderbook:'/market/books?sz=20&instId=',trades:'/market/trades?limit=30&instId=',
  funding:'/public/funding-rate?instId=',open_interest:'/public/open-interest?instType=SWAP&instId='
 };
 if(!endpoints[operation])throw new Error('market_operation_not_supported');
 const root='https://www.okx.com/api/v5';
 const url=root+endpoints[operation]+encodeURIComponent(instId);
 const x=await parse(url,450000);
 if(x.data?.code!=='0'||!Array.isArray(x.data?.data))throw new Error('OKX_public_endpoint_unavailable');
 return {operation,instrument:instId,observations:x.data.data.slice(0,60),dataSource:x.source,
  fetchedAt:x.fetchedAt,readOnly:true,noOrdersPlaced:true};
}
export async function publicCompanySearch(input:unknown){
 const d=input&&typeof input==='object'&&!Array.isArray(input)?input as R:{};
 const query=clean(d.query);
 if(query.length<2||query.length>120)throw new Error('invalid_company_query');
 const url='https://en.wikipedia.org/w/api.php?action=query&list=search&format=json&srlimit=10&srsearch='+encodeURIComponent(query);
 const r=await parse(url,300000);
 const results=Array.isArray(r.data?.query?.search)?r.data.query.search:[];
 const strip=(s:unknown)=>clean(s).replace(/<[^>]+>/g,'').slice(0,380);
 return {query,results:results.map((a:R)=>({title:strip(a.title),
  description:strip(a.snippet),pageId:a.pageid,
  sourceUrl:'https://en.wikipedia.org/?curid='+encodeURIComponent(String(a.pageid))})),
  source:r.source,asOf:r.fetchedAt,notCRMVerified:true};
}
export async function publicGithubResearch(input:unknown){
 const d=input&&typeof input==='object'&&!Array.isArray(input)?input as R:{};
 const query=clean(d.query);
 if(query.length<2||query.length>110)throw new Error('invalid_repository_query');
 const url='https://api.github.com/search/repositories?q='+encodeURIComponent(query)+'&per_page=10';
 const r=await parse(url,400000);
 return {query,source:r.source,fetchedAt:r.fetchedAt,
  repositories:(r.data.items||[]).slice(0,10).map((x:R)=>({
   name:x.full_name,description:x.description,url:x.html_url,
   stars:x.stargazers_count,updatedAt:x.updated_at})),
  scope:'public_github_repositories_only'};
}
export async function sportsOdds(input:unknown){
 const d=input&&typeof input==='object'&&!Array.isArray(input)?input as R:{};
 const league=clean(d.league||'nba').toLowerCase();
 const leagues:Record<string,string>={
  nba:'basketball/nba',wnba:'basketball/wnba',ncaab:'basketball/mens-college-basketball',
  nfl:'football/nfl',ncaaf:'football/college-football',
  nhl:'hockey/nhl',mlb:'baseball/mlb',soccer:'soccer/usa.1',
 };
 if(!leagues[league])throw new Error('unsupported_free_sports_league');
 const url='https://site.api.espn.com/apis/site/v2/sports/'+leagues[league]+'/scoreboard?limit=60';
 const r=await parse(url,750000);
 const ev=Array.isArray(r.data?.events)?r.data.events:[];
 const events=ev.slice(0,50).map((e:R)=>{
  const competition=Array.isArray(e.competitions)?e.competitions[0] as R||{}:{};
  const competitors=Array.isArray(competition.competitors)?competition.competitors as R[]:[];
  const odds=Array.isArray(competition.odds)?competition.odds as R[]:[];
  return {eventId:e.id,scheduledAt:e.date,status:(e.status as R)?.type||e.status,
   competitors:competitors.map(t=>({team:(t.team as R)?.displayName,homeAway:t.homeAway,score:t.score})),
   publishedOdds:odds.map(o=>({provider:(o.provider as R)?.name||null,
    details:o.details||null,overUnder:o.overUnder||null,spread:o.spread||null})),
   oddsAvailable:odds.length>0};
 });
 return {league,events,source:r.source,fetchedAt:r.fetchedAt,
  oddsCoverage:events.filter((row:{oddsAvailable:boolean})=>row.oddsAvailable).length,
  note:'Only publicly published ESPN scoreboard odds are returned. No multi-book historical odds are fabricated.',
  noBetsPlaced:true};
}

export function csvDecode(text:string,maxRows=5000):R[]{
 if(text.length>Math.min(4000000,Math.max(1500000,maxRows*100)))throw new Error('CSV too large');
 const output:string[][]=[];let cells:string[]=[],field='',quoted=false;
 for(let i=0;i<text.length;i++){
  const ch=text[i];if(ch==='"'){
   if(quoted&&text[i+1]==='"'){field+='"';i++}else quoted=!quoted;
  }else if(ch===','&&!quoted){cells.push(field);field='';}
  else if((ch==='\n'||ch==='\r')&&!quoted){if(ch==='\r'&&text[i+1]==='\n')i++;
   cells.push(field);field='';if(cells.some(x=>x.trim()))output.push(cells);
   cells=[];if(output.length>maxRows+1)throw new Error('CSV row cap');
  }else{field+=ch;if(field.length>20000)throw new Error('CSV field too long');}
 }
 if(quoted)throw new Error('malformed CSV quotes');
 cells.push(field);if(cells.some(x=>x.trim()))output.push(cells);
 const header=output.shift()?.map((h,i)=>i===0?h.replace(/^\uFEFF/,'').trim():h.trim());
 if(!header?.length)throw new Error('CSV without header');
 return output.map(line=>Object.fromEntries(header.map((name,i)=>[name,line[i]??''])));
}
const pick=(r:R,...names:string[]):number|null=>{
 for(const n of names){if(n in r){const x=finite(r[n]);if(x!==null)return x;}}
 return null;
};
const sign=(n:number)=>n>0?1:n<0?-1:0;
const percentile=(a:number[],v:number)=>100*a.filter(x=>x<=v).length/(a.length||1);
const weighted=(items:{name:string;weight:number;score:number|null}[])=>{
 const valid=items.filter(x=>x.score!==null);
 const total=valid.reduce((s,x)=>s+x.weight,0);
 if(!total)throw new Error('not_enough_market_indicators');
 return {score:clamp(valid.reduce((s,x)=>s+(x.score||0)*x.weight,0)/total),
  components:items.map(x=>({...x,effectiveWeight:x.score===null?0:x.weight/total}))};
};
export async function marketBreadthLive(){
 const url='https://tradermonty.github.io/market-breadth-analysis/market_breadth_data.csv';
 const file=await getPublic(url,1450000);
 const data=csvDecode(file.text).filter(x=>clean(x.Date));
 if(data.length<40)throw new Error('breadth_history_too_short');
 const latest=data[data.length-1],prev=data[data.length-11];
 const b8=pick(latest,'Breadth_Index_8MA'),b200=pick(latest,'Breadth_Index_200MA');
 if(b8===null||b200===null)throw new Error('breadth_source_schema_changed');
 const b8old=pick(prev,'Breadth_Index_8MA')??b8;
 const diff=b8-b200;
 const p=pick(latest,'S&P500_Price'),pOld=pick(data[data.length-21],'S&P500_Price');
 const oldBreadth=pick(data[data.length-21],'Breadth_Index_8MA');
 const historical=data.map(x=>pick(x,'Breadth_Index_8MA')).filter((x):x is number=>x!==null);
 const peak=pick(latest,'Is_Peak')===1,trough=pick(latest,'Is_Trough')===1;
 const bearish=pick(latest,'Bearish_Signal');
 const components=[
  {name:'Breadth Level & Trend',weight:0.25,score:clamp(b8*100+(b8-b8old)*60)},
  {name:'8MA vs 200MA',weight:0.2,score:clamp(50+diff*120)},
  {name:'Peak/Trough Cycle',weight:0.2,score:peak?30:trough?65:clamp(50+(b8-b200)*30)},
  {name:'Bearish Signal',weight:0.15,score:bearish===null?null:bearish===0?100:0},
  {name:'Historical Percentile',weight:0.1,score:historical.length>40?percentile(historical,b8):null},
  {name:'S&P 500 Divergence',weight:0.1,score:p!==null&&pOld&&oldBreadth!==null?
   clamp(50+50*sign((p-pOld)*(b8-oldBreadth))):null}
 ];
 const result=weighted(components);
 return {...result,dataDate:latest.Date,dataSource:file.url,fetchedAt:file.fetchedAt,observations:data.length,
  zone:result.score>=80?'Strong':result.score>=60?'Healthy':result.score>=40?'Neutral':
   result.score>=20?'Weakening':'Critical',
  sourceMethod:'Six-component evidence-based summary; not a byte-for-byte execution of original Python script.',
  freshnessDays:Math.floor((Date.now()-Date.parse(clean(latest.Date)))/86400000),
  readOnly:true};
}
export async function marketUptrendLive(){
 const base='https://raw.githubusercontent.com/tradermonty/uptrend-dashboard/main/data/';
 const [a,b]=await Promise.all([
  getPublic(base+'uptrend_ratio_timeseries.csv',1200000),
  getPublic(base+'sector_summary.csv',450000)
 ]);
 const history=csvDecode(a.text),sectors=csvDecode(b.text);
 if(history.length<10||sectors.length<3)throw new Error('uptrend_history_or_sectors_unavailable');
 const last=history[history.length-1],older=history[Math.max(0,history.length-11)];
 const ratio=Object.entries(last).filter(([k])=>/ratio/i.test(k)).map(([,v])=>finite(v)).find(x=>x!==null);
 if(ratio===undefined||ratio===null)throw new Error('uptrend_schema_changed');
 const olds=Object.entries(older).filter(([k])=>/ratio/i.test(k)).map(([,v])=>finite(v)).find(x=>x!==null);
 const sectorSummary=sectors.slice(0,20).map(r=>{
  const score=Object.entries(r).find(([key,val])=>/ratio/i.test(key)&&finite(val)!==null);
  return {sector:clean(r.Sector||r.sector||r.Name||r.name),
   ratio:score?finite(score[1]):null};
 });
 const valid=sectorSummary.map(x=>x.ratio).filter((x):x is number=>x!==null);
 const spread=valid.length?Math.max(...valid)-Math.min(...valid):null;
 const scaledRatio=ratio<=1?ratio*100:ratio;
 const pct=history.map(r=>Object.entries(r).find(([k,v])=>/ratio/i.test(k)&&finite(v)!==null))
  .filter((x):x is [string,unknown]=>!!x).map(x=>finite(x[1])||0);
 const parts=[
  {name:'Market Breadth',weight:0.3,score:clamp(scaledRatio)},
  {name:'Sector Participation',weight:0.25,score:valid.length?clamp(valid.filter(x=>x>(x<=1?0.5:50)).length/valid.length*100):null},
  {name:'Sector Rotation',weight:0.15,score:valid.length?clamp(100-(spread||0)):null},
  {name:'Momentum',weight:0.2,score:olds!==undefined&&olds!==null?clamp(50+(ratio-olds)*100):null},
  {name:'Historical Percentile',weight:0.1,score:pct.length>=10?percentile(pct,ratio):null}
 ];
 const result=weighted(parts);
 return {...result,latestRatio:ratio,sectors:sectorSummary,
  momentumChange:olds===null||olds===undefined?null:ratio-olds,ratioSpread:spread,
  zone:result.score>=80?'Strong Bull':result.score>=60?'Bull':result.score>=40?'Neutral':
   result.score>=20?'Cautious':'Bear',
  sourceUrls:[a.url,b.url],fetchedAt:a.fetchedAt,historyPoints:history.length,readOnly:true,
  sourceMethod:'Five-component live CSV analyzer with published weighting, no external writes.'};
}
export async function seoLive(input:unknown){
 const d=input&&typeof input==='object'&&!Array.isArray(input)?input as R:{};
 const target=clean(d.url);
 let parsed:URL;try{parsed=new URL(target)}catch{throw new Error('invalid_website_URL');}
 if(parsed.protocol!=='https:'||parsed.username||parsed.password||parsed.port&&parsed.port!=='443'
 ||parsed.hostname.length>230||!parsed.hostname.includes('.')
 ||/^(?:\d+\.){3}\d+$/.test(parsed.hostname)
 ||/(?:^|\.)(?:localhost|internal|local|test|onion|lan)$/.test(parsed.hostname))
 throw new Error('public_https_website_required');
 const categories=['SEO','PERFORMANCE','ACCESSIBILITY','BEST_PRACTICES'];
 const url='https://www.googleapis.com/pagespeedonline/v5/runPagespeed?strategy=mobile&'+
   categories.map(x=>'category='+encodeURIComponent(x)).join('&')+'&url='+encodeURIComponent(parsed.href);
 const r=await parse(url,1500000);
 const cats=r.data?.lighthouseResult?.categories||{};
 const audits=r.data?.lighthouseResult?.audits||{};
 return {url:parsed.href,fetchTime:r.fetchedAt,source:r.source,
  scores:Object.fromEntries(Object.entries(cats).map(([key,val])=>
   [key,Math.round((finite((val as R).score)||0)*100)])),
  findings:Object.entries(audits).filter(([,v])=>(v as R)?.score!==null&&
   (typeof (v as R)?.score==='number')&&((v as R).score as number)<0.9)
   .slice(0,45).map(([id,val])=>({id,title:(val as R).title,score:(val as R).score,
    description:String((val as R).description||'').slice(0,400)})),
  reportedFinalUrl:r.data?.lighthouseResult?.finalUrl||null,
  note:'Google PageSpeed Insights free public audit. No page was edited.'};
}
